import { useAuth } from "@/context/authContext";
import { api } from "@/utils/api";
import { useState, type Dispatch, type SetStateAction } from "react";
import { toast } from "sonner";

const ARCHIVED_PARENT_MESSAGE =
  "Cannot modify archived rotation. Unarchive it first.";

const reportError = (error: any) => {
  if (error?.message === ARCHIVED_PARENT_MESSAGE) {
    toast.error("This rotation has been archived. Please refresh.");
    return;
  }
  toast.error(error.message);
};

export const useSignUp = () => {
  const { dispatch } = useAuth();
  const [loading, setLoading] = useState<{
    loadRotations: boolean;
    loadExplorations: boolean;
    signUp: boolean;
    updateSignUp: boolean;
    removeSignUp: boolean;
    preferences: boolean;
    updatePreferences: boolean;
    removePreferences: boolean;
  }>({
    loadRotations: false,
    loadExplorations: false,
    signUp: false,
    updateSignUp: false,
    removeSignUp: false,
    preferences: false,
    updatePreferences: false,
    removePreferences: false,
  });

  const loadRotations = async (
    setRotations: Dispatch<
      SetStateAction<{
        rotations: any[];
        sessions: any[];
        enrollments: any[];
        preferences: any[];
      }>
    >,
  ) => {
    try {
      setLoading((prev) => ({ ...prev, loadRotations: true }));
      const response = await api(
        "/rotations?shape=student",
        { method: "GET" },
        dispatch,
      );
      setRotations({
        rotations: response.rotations,
        sessions: response.sessions,
        enrollments: response.enrollments,
        preferences: response.preferences,
      });
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading((prev) => ({ ...prev, loadRotations: false }));
    }
  };

  const loadExplorations = async (
    sessionId: string,
    setExplorations: Dispatch<SetStateAction<any[]>>,
  ) => {
    try {
      setLoading((prev) => ({ ...prev, loadExplorations: true }));
      const response = await api(
        `/explorations?shape=student&session=${sessionId}`,
        { method: "GET" },
        dispatch,
      );
      setExplorations(response.explorations);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading((prev) => ({ ...prev, loadExplorations: false }));
    }
  };

  const signUp = async (sessionId: string, explorationId: string) => {
    try {
      setLoading((prev) => ({ ...prev, signUp: true }));
      const response = await api(
        `/enrollments/enrollment/${sessionId}/${explorationId}`,
        {
          method: "POST",
        },
        dispatch,
      );
      toast.success(response.message);
      return response.enrollment;
    } catch (error: any) {
      reportError(error);
    } finally {
      setLoading((prev) => ({ ...prev, signUp: false }));
    }
  };

  const updateSignUp = async (sessionId: string, explorationId: string) => {
    try {
      setLoading((prev) => ({ ...prev, updateSignUp: true }));
      const response = await api(
        `/enrollments/enrollment/${sessionId}`,
        {
          method: "PUT",
          body: { explorationId },
        },
        dispatch,
      );
      toast.success(response.message);
      return response.enrollment;
    } catch (error: any) {
      reportError(error);
    } finally {
      setLoading((prev) => ({ ...prev, updateSignUp: false }));
    }
  };

  const removeSignUp = async (sessionId: string) => {
    try {
      setLoading((prev) => ({ ...prev, removeSignUp: true }));
      const response = await api(
        `/enrollments/enrollment/${sessionId}`,
        {
          method: "DELETE",
        },
        dispatch,
      );
      toast.success(response.message);
      return response._id;
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading((prev) => ({ ...prev, removeSignUp: false }));
    }
  };

  const preferences = async (sessionId: string, choices: string[]) => {
    try {
      setLoading((prev) => ({ ...prev, preferences: true }));
      const response = await api(
        `/enrollments/preferences/${sessionId}`,
        {
          method: "POST",
          body: {
            choices,
          },
        },
        dispatch,
      );
      toast.success(response.message);
      return response.preferences;
    } catch (error: any) {
      reportError(error);
    } finally {
      setLoading((prev) => ({ ...prev, preferences: false }));
    }
  };

  const updatePreferences = async (sessionId: string, choices: string[]) => {
    try {
      setLoading((prev) => ({ ...prev, updatePreferences: true }));
      const response = await api(
        `/enrollments/preferences/${sessionId}`,
        {
          method: "PUT",
          body: {
            choices,
          },
        },
        dispatch,
      );
      toast.success(response.message);
      return response.preferences;
    } catch (error: any) {
      reportError(error);
    } finally {
      setLoading((prev) => ({ ...prev, updatePreferences: false }));
    }
  };

  const removePreferences = async (sessionId: string) => {
    try {
      setLoading((prev) => ({ ...prev, removePreferences: true }));
      const response = await api(
        `/enrollments/preferences/${sessionId}`,
        {
          method: "DELETE",
        },
        dispatch,
      );
      toast.success(response.message);
      return response._id;
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading((prev) => ({ ...prev, removePreferences: false }));
    }
  };

  return {
    loadRotations,
    loadExplorations,
    signUp,
    updateSignUp,
    removeSignUp,
    preferences,
    updatePreferences,
    removePreferences,
    loading,
  };
};
